import { useMemo, useState } from "react";
import useSeo from "@/hooks/useSeo";
import MatchCard from "@/components/feature/MatchCard";
import FilterBar from "./components/FilterBar";
import { matches } from "@/mocks/matches";
import { formatDateCN, uniqueDates } from "@/utils";

const PAGE_SIZE = 6;

export default function Live() {
  useSeo({
    title: "NBA直播吧 - NBA直播分类",
    description:
      "NBA直播分类页，按比赛日期、球队与直播状态筛选今日NBA直播比赛，快速找到正在直播的比赛并进入直播间观看。",
    keywords: "NBA直播分类,NBA比赛直播,NBA直播时间,NBA球队直播,篮球直播",
  });

  const dates = uniqueDates(matches.map((m) => m.date));
  const [date, setDate] = useState("全部");
  const [team, setTeam] = useState("全部");
  const [status, setStatus] = useState("全部");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [mobileFilter, setMobileFilter] = useState(false);

  const filtered = useMemo(() => {
    const order: Record<string, number> = { live: 0, upcoming: 1, finished: 2 };
    return matches
      .filter((m) => date === "全部" || m.date === date)
      .filter((m) => team === "全部" || m.home === team || m.away === team)
      .filter((m) => status === "全部" || m.status === status)
      .sort((a, b) => {
        if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.time.localeCompare(b.time);
      });
  }, [date, team, status]);

  const shown = filtered.slice(0, visible);
  const liveCount = filtered.filter((m) => m.status === "live").length;

  const resetFilter = () => {
    setDate("全部");
    setTeam("全部");
    setStatus("全部");
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs text-foreground-400 font-label mb-2">
          <i className="ri-home-4-line"></i> 首页 <i className="ri-arrow-right-s-line"></i> <span className="text-foreground-600">NBA直播</span>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">NBA直播</h1>
            <p className="text-sm text-foreground-500 mt-1">
              共找到 <span className="text-primary-600 font-semibold">{filtered.length}</span> 场比赛，其中
              <span className="text-accent-600 font-semibold"> {liveCount} </span>场正在直播
            </p>
          </div>
          <button
            type="button"
            onClick={() => setMobileFilter((v) => !v)}
            className="md:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-800 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-filter-3-line"></i> 筛选
          </button>
        </div>
      </div>

      <div className={`${mobileFilter ? "block" : "hidden"} md:block mb-6`}>
        <FilterBar
          dates={dates}
          date={date}
          team={team}
          status={status}
          onDate={(v) => {
            setDate(v);
            setVisible(PAGE_SIZE);
          }}
          onTeam={(v) => {
            setTeam(v);
            setVisible(PAGE_SIZE);
          }}
          onStatus={(v) => {
            setStatus(v);
            setVisible(PAGE_SIZE);
          }}
          formatDate={formatDateCN}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-background-50 rounded-lg border border-background-200 py-20 flex flex-col items-center gap-4 text-center">
          <span className="w-14 h-14 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl">
            <i className="ri-live-line"></i>
          </span>
          <div>
            <p className="text-foreground-900 font-medium">暂无符合条件的直播比赛</p>
            <p className="text-sm text-foreground-500 mt-1">试试调整日期、球队或状态筛选条件</p>
          </div>
          <button
            type="button"
            onClick={resetFilter}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i> 重置筛选
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop>
            {shown.map((m) => (
              <MatchCard key={m.id} match={m} variant="card" />
            ))}
          </div>

          {visible < filtered.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-100 border border-background-300 text-sm font-medium text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i> 加载更多（剩余 {filtered.length - visible} 场）
              </button>
            </div>
          )}
          {visible >= filtered.length && filtered.length > PAGE_SIZE && (
            <p className="mt-8 text-center text-xs text-foreground-400">已展示全部 {filtered.length} 场比赛</p>
          )}
        </>
      )}
    </div>
  );
}